import { formatCurrencyDisplay, normalizeCurrencyDisplay } from "../utils/currencyDisplay.js";
import {
  formatTonsWithUnit,
  getTruckCompatibilityTimeWindowMode,
  isTruckCompatibilityActionable,
} from "../utils/resultsFleetData.js";

export function formatCurrency(value) {
  return formatCurrencyDisplay(value) || "-";
}

export function formatDelta(value, formatter) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }

  const sign = value > 0 ? "+" : "";
  return `${sign}${formatter(value)}`;
}

export function formatDurationMinutes(value) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "-";
  }

  const roundedMinutes = Math.round(value);
  if (roundedMinutes < 60) {
    return `${roundedMinutes} min`;
  }

  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  if (!minutes) {
    return `${hours} h`;
  }

  return `${hours} h ${minutes} min`;
}

export { formatTonsWithUnit };

export function getDeltaToneClass(value) {
  if (typeof value !== "number" || Number.isNaN(value) || value === 0) {
    return "text-muted-strong";
  }

  return value > 0 ? "text-danger" : "text-success";
}

export function getAssignmentMeta(summary) {
  if (!summary) {
    return null;
  }

  const parts = [
    Number.isInteger(summary?.trip_count)
      ? `${summary.trip_count} trajet${summary.trip_count > 1 ? "s" : ""}`
      : null,
    formatTonsWithUnit(summary?.total_quantity_tons),
  ].filter(Boolean);

  return parts.length ? parts.join(" • ") : null;
}

export function getImpactToneClasses(tone) {
  if (tone === "target") {
    return {
      cardClassName: "border-[rgba(21,37,36,0.12)] border-l-[3px] border-l-[rgba(21,128,61,0.52)] bg-[rgba(228,239,231,0.98)]",
      pillClassName: "border-[rgba(21,37,36,0.1)] bg-[rgba(237,245,239,0.98)]",
      titleClassName: "text-[rgba(21,128,61,0.92)]",
    };
  }

  if (tone === "source") {
    return {
      cardClassName: "border-[rgba(21,37,36,0.12)] border-l-[3px] border-l-[rgba(180,83,9,0.5)] bg-[rgba(241,234,228,0.98)]",
      pillClassName: "border-[rgba(21,37,36,0.1)] bg-[rgba(247,240,235,0.98)]",
      titleClassName: "text-[rgba(146,64,14,0.92)]",
    };
  }

  return {
    cardClassName: "border-[rgba(21,37,36,0.12)] border-l-[3px] border-l-[rgba(71,85,105,0.44)] bg-[rgba(229,235,241,0.98)]",
    pillClassName: "border-[rgba(21,37,36,0.1)] bg-[rgba(237,241,246,0.98)]",
    titleClassName: "text-[rgba(71,85,105,0.94)]",
  };
}

export default function useTruckCompatibilityViewModel({ compatibility }) {
  const routeImpact = compatibility?.route_impact;
  const truckSnapshot = compatibility?.truck_snapshot;
  const timeWindowMode = getTruckCompatibilityTimeWindowMode(compatibility);
  const requiresTimeWindowOverride = timeWindowMode === "override_required";
  const requiresTimeWindowConfirmation = timeWindowMode === "soft";
  const isActionable = isTruckCompatibilityActionable(compatibility);
  const hasTimeWindowRequirement = requiresTimeWindowOverride || requiresTimeWindowConfirmation;
  const hasRouteImpact = Boolean(
    routeImpact?.source_after
    || routeImpact?.target_after
    || typeof routeImpact?.fleet_cost_delta_cad === "number"
    || typeof routeImpact?.fleet_duration_delta_minutes === "number",
  );
  const detailText = (hasTimeWindowRequirement || !isActionable || compatibility?.reason_code === "time_warning")
    ? normalizeCurrencyDisplay(compatibility?.detail ?? "Aucune precision.")
    : null;
  const stateLabel = !isActionable ? "Indisponible" : null;
  const stateToneClass = "bg-neutral-soft text-muted-strong";
  const requirementBadge = requiresTimeWindowOverride
    ? {
      className: "border border-[rgba(180,83,9,0.16)] bg-[rgba(255,247,237,0.96)] text-[rgba(154,52,18,0.92)]",
      label: "Motif requis",
    }
    : requiresTimeWindowConfirmation
      ? {
        className: "border border-[rgba(217,119,6,0.16)] bg-[rgba(255,251,235,0.96)] text-[rgba(161,98,7,0.92)]",
        label: "Confirmation requise",
      }
      : null;

  return {
    detailText,
    hasRouteImpact,
    isActionable,
    requirementBadge,
    routeImpact,
    actionLabel: requiresTimeWindowOverride ? "Justifier" : requiresTimeWindowConfirmation ? "Confirmer" : "Assigner",
    stateLabel,
    stateToneClass,
    timeWindowDiagnostics: Array.isArray(compatibility?.time_window_diagnostics) ? compatibility.time_window_diagnostics.filter(Boolean) : [],
    timeWindowMode,
    truckSnapshot,
  };
}
